import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandLogo } from '@tailoring-crm/ui';

/**
 * The frame every screen before the application shares.
 *
 * Single column, 360px-first, and vertically top-aligned rather than centred: a centred card
 * jumps up the screen the moment the software keyboard opens, which on a phone is the entire
 * time somebody is using this. `min-h-dvh` (not `vh`) is the same fight — `vh` on mobile
 * Safari measures a viewport that does not exist once the address bar collapses.
 *
 * The step counter is not decoration either: signup is three screens, and a person who cannot
 * see how many are left assumes the worst.
 */
export interface AuthLayoutProps {
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  step?: { current: number; total: number };
}

export function AuthLayout({
  title,
  description,
  children,
  footer,
  step,
}: AuthLayoutProps): ReactElement {
  const { t } = useTranslation();

  return (
    <div className="min-h-dvh bg-background px-4 pb-10 pt-8 sm:pt-16">
      <div className="mx-auto flex w-full max-w-sm flex-col gap-6">
        <header className="flex flex-col gap-3">
          <BrandLogo className="h-8 self-start" />

          {step ? (
            <p className="text-sm text-muted-foreground">
              {t('auth.stepCounter', { current: step.current, total: step.total })}
            </p>
          ) : null}

          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold leading-tight">{title}</h1>
            {description ? <p className="text-muted-foreground">{description}</p> : null}
          </div>
        </header>

        <main>{children}</main>

        {footer ? <footer className="text-sm text-muted-foreground">{footer}</footer> : null}
      </div>
    </div>
  );
}
